import { useState, useEffect } from 'react';
import { API_BASE } from '../config';

const API_BASE_URL = `${API_BASE}/api`;

/**
 * Custom hook to fetch and manage zone events for a run
 * @param {number|null} runId - The ID of the run to fetch events for
 * @returns {Object} Zone events data and loading state
 */
export const useZoneEvents = (runId) => {
  const [zoneEvents, setZoneEvents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Reset state when runId changes
    if (!runId) {
      console.log('[useZoneEvents] No runId provided, clearing events');
      setZoneEvents(null);
      setError(null);
      return;
    }

    const fetchZoneEvents = async () => {
      setLoading(true);
      setError(null);
      
      // Clear previous events immediately when switching runs
      setZoneEvents(null);

      try {
        console.log(`[useZoneEvents] Fetching zone events for run ${runId}`);
        
        const response = await fetch(`${API_BASE_URL}/runs/${runId}/zone-events`);
        
        console.log(`[useZoneEvents] Response status: ${response.status}`);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error(`[useZoneEvents] Error response:`, errorText);
          throw new Error(`HTTP ${response.status}: Failed to fetch zone events`);
        }

        const data = await response.json();
        console.log(`[useZoneEvents] Run ${runId}: Fetched ${data.count || 0} zone events`, data);

        // Validate it's a FeatureCollection
        if (data.type === 'FeatureCollection' && Array.isArray(data.features)) {
          console.log(`[useZoneEvents] Run ${runId}: Valid FeatureCollection with ${data.features.length} features`);
          
          // Log first event for debugging
          if (data.features.length > 0) {
            console.log(`[useZoneEvents] Run ${runId}: First event:`, data.features[0]);
          } else {
            console.warn(`[useZoneEvents] Run ${runId}: No events in features array`);
          }
          
          setZoneEvents(data);
        } else {
          console.warn('[useZoneEvents] Invalid response format:', data);
          setZoneEvents({ type: 'FeatureCollection', features: [], count: 0 });
        }
      } catch (err) {
        const errorMsg = err.message || 'Failed to fetch zone events';
        setError(errorMsg);
        console.error(`[useZoneEvents] Error fetching events for run ${runId}:`, err);
        setZoneEvents(null);
      } finally {
        setLoading(false);
      }
    };

    fetchZoneEvents();
  }, [runId]);

  return {
    zoneEvents,
    loading,
    error
  };
};